import { useRouter } from 'next/router'
import { ReactElement } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'

import { RootState, useSelector, useDispatch } from '@/store'
import { setContactMedia } from '@/store/contactMedia/slice'
import { ContactMedia } from '@/store/contactMedia/types'
import { theme } from '@/styles/theme'

import { mediaLabels } from './MediaLabel'
import { Button } from '@/components/elements/Button'
import { FormInput } from '@/components/elements/FormInput'
import { FormLabel } from '@/components/elements/FormLabel'
import { LinkText } from '@/components/elements/LinkText'
import { SectionTitle } from '@/components/elements/SectionTitle'
import { TitleLayout } from '@/components/layout'
import { Head } from '@/components/layouts/Head'

export const MediaPage = () => {
  const form = useSelector((state: RootState) => state.contactMedia)
  const dispatch = useDispatch()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactMedia>()
  const router = useRouter()

  const onSubmit = (data: ContactMedia) => {
    dispatch(setContactMedia(data))
    router.push('/contact/media/confirm')
  }

  return (
    <>
      <Head
        title="取材等に関するお問い合わせ"
        description="トライアルホールディングスの取材等に関するお問い合わせはこちらから。"
        ogp={{ type: 'article', url: 'contact/media/' }}
      />
      <MediaMain>
        <SectionTitle
          titleText="Media"
          subTitleText="メディア"
          color="normal"
        />
        <Note>
          報道関係のみなさまからの取材や講演等の申し込みに関するご連絡専用フォームです。
          <br />
          その他のご質問やお問合せをいただきましても、お返事を差し上げることはできませんのであらかじめご了承ください。
          <br />
          下記に必要事項をご記入の上お問合せください。
        </Note>
        <Form method="post" noValidate onSubmit={handleSubmit(onSubmit)}>
          <FormBackground>
            <FormMain>
              <FormLabelWrapper>
                <FormLabel {...mediaLabels.company} />
              </FormLabelWrapper>
              <FormInput
                id="company"
                type="text"
                register={register('company')}
                defaultValue={form.company}
              />
              <FormLabelWrapper>
                <FormLabel {...mediaLabels.fullName} />
              </FormLabelWrapper>
              <FormInner>
                <FormGroup>
                  <FormLabel {...mediaLabels.lastName} />
                  <FormInput
                    id="lastName"
                    type="text"
                    register={register('lastName', {
                      required: {
                        value: true,
                        message: '入力してください。',
                      },
                    })}
                    errorMessage={errors.lastName}
                    defaultValue={form.lastName}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel {...mediaLabels.firstName} />
                  <FormInput
                    id="firstName"
                    type="text"
                    register={register('firstName', {
                      required: {
                        value: true,
                        message: '入力してください。',
                      },
                    })}
                    errorMessage={errors.firstName}
                    defaultValue={form.firstName}
                  />
                </FormGroup>
              </FormInner>
              <FormLabelWrapper>
                <FormLabel {...mediaLabels.email} />
              </FormLabelWrapper>
              <FormInput
                id="email"
                type="email"
                register={register('email', {
                  required: {
                    value: true,
                    message: '入力してください。',
                  },
                  pattern: {
                    message: '正しい形式で入力してください。',
                    value:
                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  },
                })}
                placeholder="例）XXX@XXX.ne.jp"
                errorMessage={errors.email}
                defaultValue={form.email}
              />
              <FormLabelWrapper>
                <FormLabel {...mediaLabels.tel} />
              </FormLabelWrapper>
              <FormInput
                id="tel"
                type="tel"
                maxLength={13}
                register={register('tel', {
                  pattern: {
                    message: '正しい形式で入力してください。',
                    value: /^[-0-9]+$/,
                  },
                })}
                placeholder="例）00-0000-0000"
                errorMessage={errors.tel}
                defaultValue={form.tel}
              />
              <FormLabelWrapper>
                <FormLabel {...mediaLabels.media} />
              </FormLabelWrapper>
              <FormInput
                id="media"
                type="text"
                register={register('media')}
                defaultValue={form.media}
              />
              <FormLabelWrapper>
                <FormLabel {...mediaLabels.title} />
              </FormLabelWrapper>
              <FormInput
                id="title"
                type="text"
                register={register('title')}
                placeholder="例）XXXについて"
                defaultValue={form.title}
              />
              <FormLabelWrapper>
                <FormLabel {...mediaLabels.detail} />
              </FormLabelWrapper>
              <FormInput
                id="detail"
                type="text"
                register={register('detail', {
                  required: {
                    value: true,
                    message: '入力してください。',
                  },
                })}
                as="textarea"
                placeholder="お問合せ内容をご入力ください。"
                errorMessage={errors.detail}
                defaultValue={form.detail}
              />
            </FormMain>
            <FormBottom>
              ご記入いただきました個人情報はお問合せ対応のためにご利用させていただきます。
              <br />
              個人情報の取り扱い詳細は「
              <LinkText
                path="/legal/privacy-policy/"
                text="プライバシーポリシー"
              />
              」をご覧ください。
            </FormBottom>
          </FormBackground>
          <ButtonWrapper>
            <SubmitButton btnStyle="outline" type="submit">
              確認する
            </SubmitButton>
          </ButtonWrapper>
        </Form>
      </MediaMain>
    </>
  )
}

MediaPage.getLayout = function getLayout(page: ReactElement) {
  const pageTitle = {
    title: 'Inquiry',
    subTitle: 'お問い合わせ',
  }

  const breadCrumbs = [
    { name: 'ホーム', path: '/' },
    { name: 'お問い合わせ', path: '/contact/' },
    { name: 'メディア', path: '' },
  ]

  return (
    <TitleLayout {...pageTitle} breadCrumbs={breadCrumbs} isRequiredBackground>
      {page}
    </TitleLayout>
  )
}

const MediaMain = styled.div`
  width: 1040px;
  padding-bottom: 130px;
  margin: 78px auto 0;

  @media screen and (max-width: 1047px) {
    width: 100%;
    margin: 40px auto 0;
    padding: 0 20px 70px;
  }
`

const Note = styled.p`
  line-height: 30px;
  letter-spacing: 0.32px;
  margin: 71px 0 77px;
  font-weight: 400;

  @media screen and (max-width: 1047px) {
    margin: 56px 0 45px;
    font-size: 15px;
    line-height: 25px;
    letter-spacing: 0.3px;
  }
`

const Form = styled.form``

const FormMain = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 40px 30px;

  @media screen and (max-width: 1047px) {
    grid-template-columns: 1fr;
    gap: 16px 0;
  }
`

const FormBackground = styled.div`
  background-color: ${theme.colors.backgroundGrey};
  padding: 86px 74px 99px;

  @media screen and (max-width: 1047px) {
    padding: 27px 20px 40px;
  }
`

const FormLabelWrapper = styled.div`
  padding-top: 14px;
`

const FormInner = styled.div`
  display: flex;
  align-items: center;
  column-gap: 43px;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    align-items: flex-start;
    row-gap: 14px;
  }
`

const FormGroup = styled.div`
  display: flex;
  align-items: center;
  column-gap: 26px;
  width: 50%;

  div {
    flex: 1;
  }

  @media screen and (max-width: 1047px) {
    width: 100%;
  }
`

const FormBottom = styled.p`
  line-height: 26px;
  letter-spacing: 0.32px;
  margin-top: 74px;
  font-weight: 400;

  @media screen and (max-width: 1047px) {
    margin-top: 36px;
    font-size: 15px;
    line-height: 25px;
    letter-spacing: 0.3px;
  }
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 107px;

  @media screen and (max-width: 1047px) {
    margin-top: 54px;
  }
`

const SubmitButton = styled(Button)`
  width: 198px;
  font-weight: 400;
  border-width: 1px;
`
